import { useRef, useEffect } from 'react';
/**
 * Invoke a function repeatedly with the latest props
 *
 * Reference
 * | Making setInterval Declarative with React Hooks | Dan Abramov
 * | https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 * @param callback Function invoked once per poll
 * @param pollsRemaining Maximum count of future callback invocations
 * @param pollInterval Delay between each callback invocation in milliseconds
 */
export const usePolling = (
  callback: Function,
  pollsRemaining: number,
  pollInterval = 1000
) => {
  // Set callback to internal useRef
  const savedCallback = useRef(null);

  // Keep track of the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Polls the latest callback
  useEffect(() => {
    const abortController = new AbortController();
    function poll() {
      savedCallback.current();
    }
    // Set timer if the counter for polls is greater than zero
    if (pollsRemaining > 0) {
      // Invoke the latest callback after a delay of pollInterval
      const timer = setTimeout(poll, pollInterval);
      // Clear this timeout after invoking the latest callback
      return () => clearTimeout(timer);
    }
    return () => abortController.abort();
  }, [pollsRemaining, pollInterval]);
};
